/* eslint-disable react/no-unstable-nested-components */
import { useEffect } from 'react';

import { CloseOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';

import { LoaderScreen } from 'components/pages/LoaderScreen';
import { useIsAuthenticated } from 'hooks/api/auth/useIsAuthenticated';
import { useGetPageConfig } from 'hooks/api/builder/useGetPageConfig';
import { usePageStorage } from 'hooks/api/builder/usePageStorage';
import { PageList } from 'types/api/pageList';

import { FadeAnimation } from '../FadeWrapper';
import { PageLayout } from '../layouts/PageLayout';
import { PageTemplate } from '../PageTemplate';

type DynamicPageRenderProps = {
    pageID: number;
    pages: PageList[];
    metaLoading: boolean;
    metaError: boolean;
};

export const DynamicPageRender = ({
    pageID,
    pages,
    metaLoading,
    metaError,
}: DynamicPageRenderProps): JSX.Element => {
    const { authenticated } = useIsAuthenticated();

    const { page, loading, error, getPage } = useGetPageConfig(
        pageID,
        authenticated
    );

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (error) {
                getPage();
            }
        }, 3000);
        return () => {
            clearTimeout(timeout);
        };
    }, [error, getPage]);

    useEffect(() => {
        if (page) {
            document.body.setAttribute('data-page-id', String(page.id));
        }
    }, [page]);

    usePageStorage(pageID);

    const { closeSnackbar, enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (page && !page.isPublished) {
            enqueueSnackbar(
                'Страница не опубликована - она будет видна только в панели администратора',
                {
                    variant: 'warning',
                    persist: true,
                    key: 'page-publish-snack',
                    action: (key) => (
                        <IconButton
                            onClick={() => closeSnackbar(key)}
                            color="inherit"
                        >
                            <CloseOutlined color="inherit" />
                        </IconButton>
                    ),
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        return () => {
            closeSnackbar('page-publish-snack');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page?.id]);

    return (
        <PageLayout
            pages={pages}
            metaLoading={metaLoading}
            metaError={metaError}
            pageConfigLen={page?.pageConfig?.length}
        >
            {loading || !page || error ? (
                <FadeAnimation>
                    <LoaderScreen positionStatic />
                </FadeAnimation>
            ) : (
                <PageTemplate page={page} />
            )}
        </PageLayout>
    );
};
